import React, { memo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';

import { AutocompleteControl } from 'components/_basic/Form/Autocomplete';
import { NumberInputControlled } from 'components/_basic/Form/NumberInput';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { BorderedCard } from 'components/BorderedCard';
import { TooltipFormImageUpload } from 'components/BusinessToolForm/components/TooltipFormImageUpload';
import { KEY_COMPETITORS_PRIORITY_OPTIONS_DEU } from 'components/BusinessToolForm/constants/deu/options_deu';
import { KEY_COMPETITORS_PRIORITY_OPTIONS } from 'components/BusinessToolForm/constants/options';
import { KEY_COMPETITORS_PRIORITY_OPTIONS_UA } from 'components/BusinessToolForm/constants/ua/options_ua';

import { concatWithDot } from 'helpers/string';

import { KEY_COMPETITORS_NAMES } from '../utils/form';

export const KeyCompetitors: React.FC<{ name: string; disabled?: boolean }> = memo(({ name, disabled = false }) => {
  const { t, i18n } = useTranslation();
  const { control } = useFormContext();

  const getChosedKeyCompetitorsOptions = (lang: string) => {
    switch (lang) {
      case 'de':
        return KEY_COMPETITORS_PRIORITY_OPTIONS_DEU;

      case 'uk':
        return KEY_COMPETITORS_PRIORITY_OPTIONS_UA;

      default:
        return KEY_COMPETITORS_PRIORITY_OPTIONS;
    }
  };

  return (
    <BorderedCard>
      <Grid container spacing={3}>
        <Grid item xs="auto">
          <TooltipFormImageUpload
            width={64}
            height={64}
            placeholder="Logo"
            name={concatWithDot(name, KEY_COMPETITORS_NAMES.FILE)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextInputControl
                control={control}
                id={concatWithDot(name, KEY_COMPETITORS_NAMES.COMPETITOR)}
                name={concatWithDot(name, KEY_COMPETITORS_NAMES.COMPETITOR)}
                label={t('marketAnalysisForm.competitors.keyCompetitors.name')}
                disabled={disabled}
                maxLength={42}
              />
            </Grid>
            <Grid item xs={6}>
              <TextInputControl
                control={control}
                id={concatWithDot(name, KEY_COMPETITORS_NAMES.WEBSITE)}
                name={concatWithDot(name, KEY_COMPETITORS_NAMES.WEBSITE)}
                label={t('marketAnalysisForm.competitors.keyCompetitors.website')}
                disabled={disabled}
                maxLength={150}
              />
            </Grid>
            <Grid item xs={6}>
              <TextInputControl
                control={control}
                id={concatWithDot(name, KEY_COMPETITORS_NAMES.LOCATION)}
                name={concatWithDot(name, KEY_COMPETITORS_NAMES.LOCATION)}
                label={t('marketAnalysisForm.competitors.keyCompetitors.location')}
                disabled={disabled}
                maxLength={150}
              />
            </Grid>
            <Grid item xs={12}>
              <TextInputControl
                multiline
                control={control}
                id={concatWithDot(name, KEY_COMPETITORS_NAMES.DESCRIPTION)}
                name={concatWithDot(name, KEY_COMPETITORS_NAMES.DESCRIPTION)}
                label={t('marketAnalysisForm.competitors.keyCompetitors.description')}
                minRows={4}
                disabled={disabled}
                maxLength={500}
              />
            </Grid>
            <Grid item xs={12}>
              <TextInputControl
                multiline
                control={control}
                id={concatWithDot(name, KEY_COMPETITORS_NAMES.POSITIONING)}
                name={concatWithDot(name, KEY_COMPETITORS_NAMES.POSITIONING)}
                label={t('marketAnalysisForm.competitors.keyCompetitors.positioning')}
                minRows={4}
                disabled={disabled}
                maxLength={500}
              />
            </Grid>
            <Grid item xs={12}>
              <TextInputControl
                multiline
                control={control}
                id={concatWithDot(name, KEY_COMPETITORS_NAMES.SALES_CHANNELS)}
                name={concatWithDot(name, KEY_COMPETITORS_NAMES.SALES_CHANNELS)}
                label={t('marketAnalysisForm.competitors.keyCompetitors.salesChannels')}
                minRows={4}
                disabled={disabled}
                maxLength={500}
              />
            </Grid>
            <Grid item xs={12}>
              <TextInputControl
                multiline
                control={control}
                id={concatWithDot(name, KEY_COMPETITORS_NAMES.PRICING_POLICY)}
                name={concatWithDot(name, KEY_COMPETITORS_NAMES.PRICING_POLICY)}
                label={t('marketAnalysisForm.competitors.keyCompetitors.pricingPolicy')}
                minRows={4}
                disabled={disabled}
                maxLength={500}
              />
            </Grid>
            <Grid item xs={6}>
              <NumberInputControlled
                control={control}
                id={concatWithDot(name, KEY_COMPETITORS_NAMES.ANNUAL_SALES_VOLUME)}
                name={concatWithDot(name, KEY_COMPETITORS_NAMES.ANNUAL_SALES_VOLUME)}
                label={t('marketAnalysisForm.competitors.keyCompetitors.annualSales')}
                adornmentType="currency"
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={6}>
              <NumberInputControlled
                control={control}
                id={concatWithDot(name, KEY_COMPETITORS_NAMES.MARKET_SHARE)}
                name={concatWithDot(name, KEY_COMPETITORS_NAMES.MARKET_SHARE)}
                label={t('marketAnalysisForm.competitors.keyCompetitors.marketShare')}
                adornmentType="percentage"
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={6}>
              <AutocompleteControl
                control={control}
                id={concatWithDot(name, KEY_COMPETITORS_NAMES.CUSTOMER_LOYALTY_LEVEL)}
                name={concatWithDot(name, KEY_COMPETITORS_NAMES.CUSTOMER_LOYALTY_LEVEL)}
                label={t('marketAnalysisForm.competitors.keyCompetitors.loyaltyLevel')}
                options={getChosedKeyCompetitorsOptions(i18n.language)}
                disabled={disabled}
                defaultValue={getChosedKeyCompetitorsOptions(i18n.language)[0]}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </BorderedCard>
  );
});
